// NOTE: hooks
import React, { lazy, Suspense, useEffect } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
// NOTE: components
import Layout from '../../components/ui/Layout'
import Loading from '../../components/ui/TWDLoading'
import ManagerSubPage from '../../components/ui/ManagerSubPage'
// NOTE: images
import homeIcon from '../../assets/img/twd/home.png'
import settingsIcon from '../../assets/img/twd/settings.png'
import exploreIcon from '../../assets/img/twd/resources.png'
import useManagerAuthorisation from './useManagerAuthorisation'
// NOTE: main pages
const Settings = lazy(() => import('./Settings'))
const HealthOverview = lazy(() => import('./HealthOverview'))
const FinanceOverview = lazy(() => import('./FinanceOverview'))
const Managers = lazy(() => import('./Managers'))
const Explore = lazy(() => import('./Explore'))
const ContentItem = lazy(() => import('./ContentItem'))
const Onboarding = lazy(() => import('./Onboarding'))
const Setup = lazy(() => import('./Setup'))
const Staff = lazy(() => import('./Staff'))
const CreateTeam = lazy(() => import('./CreateTeam'))

// NOTE: Business copy - the home links and manager routes when from staff to members

const navLinks = [
	{
		to: '/team',
		text: 'Home',
		icon: homeIcon
	},
	{
		to: '/explore',
		text: 'Explore',
		icon: exploreIcon
	},
	{
		to: '/settings',
		text: 'Settings',
		icon: settingsIcon
	}
]

const homeLinks = [
	{
		to: 'members',
		text: 'Members'
	},
	{
		to: 'health',
		text: 'Health'
	},
	{
		to: 'finance',
		text: 'Finance'
	},
	{
		to: 'managers',
		text: 'Managers'
	},
	{
		to: 'setup',
		text: 'Setup'
	}
]

interface ProtectedManagerProps {
	isAuthorisationNeeded?: boolean
}

const ProtectedManager = ({ isAuthorisationNeeded = true }: ProtectedManagerProps) => {
	const { loading, getManagerData } = useManagerAuthorisation()

	useEffect(() => {
		getManagerData(isAuthorisationNeeded)
	}, [])

	if (loading) return <Loading />

	return <Outlet />
}

// NOTE: component to show loading animation
const LoadPage = ({ page }: any) => <Suspense fallback={<Loading />}>{page}</Suspense>

const managerRoutes = [
	{
		element: <ProtectedManager />,
		children: [
			{
				element: <Layout links={navLinks} home="/" dashboard="manager" />,
				children: [
					{
						index: true,
						element: <Navigate to="/team" />
					},
					{
						path: 'team',
						element: <ManagerSubPage title="Home" src={homeIcon} links={homeLinks} />,
						children: [
							{
								index: true,
								element: <Navigate to="members" />
							},
							{
								path: 'members',
								element: <LoadPage page={<Staff />} />
							},
							{
								path: 'health',
								element: <LoadPage page={<HealthOverview />} />
							},
							{
								path: 'finance',
								element: <LoadPage page={<FinanceOverview />} />
							},
							{
								path: 'managers',
								element: <LoadPage page={<Managers />} />
							},
							{
								path: 'setup',
								element: <LoadPage page={<Setup />} />
							}
						]
					},
					{
						path: 'explore',
						element: <LoadPage page={<Explore />} />
					},
					{
						path: 'explore/:contentId',
						element: <LoadPage page={<ContentItem />} />
					},
					{
						path: 'settings',
						element: <LoadPage page={<Settings />} />
					}
				]
			}
		]
	},
	{
		element: <ProtectedManager isAuthorisationNeeded={false} />,
		children: [
			{
				path: 'onboarding',
				element: <LoadPage page={<Onboarding />} />
			},
			{
				path: 'create-team',
				element: <LoadPage page={<CreateTeam />} />
			}
		]
	}
]

export default managerRoutes
